import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { getPreferredTheme, setTheme, getEffectiveTheme } from '../utils/theme';

// Define ThemeContextType
interface ThemeContextType {
  themePreference: boolean | null; // true = dark, false = light, null = system
  isDarkMode: boolean; // the effective theme (considering system preference)
  toggleTheme: () => void;
}

// Create context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Provider component
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [themePreference, setThemePreference] = useState<boolean | null>(null);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false); // Default initialized value will be replaced in useEffect
  
  // Function to update the effective theme
  const updateEffectiveTheme = useCallback(() => {
    const effectiveTheme = getEffectiveTheme();
    setIsDarkMode(effectiveTheme);
    
    // Make sure the correct class is applied to the document
    if (effectiveTheme) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }
  }, []);
  
  // Initialize theme on mount
  useEffect(() => {
    const preference = getPreferredTheme();
    setThemePreference(preference);
    
    // Initial setup of theme
    setTheme(preference);
    updateEffectiveTheme();
  }, [updateEffectiveTheme]);
  
  // Setup media query listener
  useEffect(() => {
    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Only update if using system preference
      if (themePreference === null) {
        updateEffectiveTheme();
      }
    };
    
    // Add listener with proper compatibility
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference, updateEffectiveTheme]); // Include themePreference in dependency array
  
  // Update effective theme when preference changes
  useEffect(() => {
    if (themePreference !== null) {
      // Directly apply the theme when it's explicitly set
      setIsDarkMode(themePreference);
    } else {
      // Check system preference when set to auto
      updateEffectiveTheme();
    }
  }, [themePreference, updateEffectiveTheme]);
  
  // Toggle theme function - based on the effective theme, not just the preference
  const toggleTheme = () => {
    // Toggle based on the current effective theme (isDarkMode), not just the preference
    if (isDarkMode) {
      // If currently dark (whether by system or explicit preference), switch to light
      setThemePreference(false);
      setTheme(false);
    } else {
      // If currently light (whether by system or explicit preference), switch to dark
      setThemePreference(true);
      setTheme(true);
    }
  };
  
  return (
    <ThemeContext.Provider value={{ themePreference, isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}; 